import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import {
  governanceDemoTotals,
  governanceRecords,
  governanceTrend,
  type GovernanceRecord,
} from '../../features/review-center/fixtures/governance-data';
import {
  applyReviewDemoAction,
  createReviewSession,
  filterReviewQueue,
  initialReviewFilters,
  noteReviewReadback,
  resetReviewDemoCase,
  reviewScope,
  reviewStatusLabels,
  updateReviewCase,
  type ReviewQueueFilters,
  type ReviewSession,
} from '../../features/review-center/fixtures/review-demo-fixtures';
import '../../features/review-center/ui/review-center.css';
import {
  ReviewQueuePrototype,
  ReviewVersionComparison,
} from '../../features/review-center/ui/ReviewQueuePrototype';
import { SynLiveAssetReviewQueue } from '../../features/review-center/ui/SynLiveAssetReviewQueue';
import { SynLiveReviewQueue } from '../../features/review-center/ui/SynLiveProductReviewQueue';
import { PortalShell } from '../../shared/platform/PortalShell';
import {
  SynLiveProductReviewProvider,
  SynLiveSessionPanel,
  synLiveEnabled,
} from '../../shared/product-review/session/ProductReviewSession';
import { AdminWorkbench } from './AdminWorkbench';

const base = '/demo/governance-review';
const queueUrl = (id: string) => `${base}/queue/${encodeURIComponent(id)}?synLive=0`;
function Status({ status }: { status: string }) {
  return (
    <span
      className={`governance-status governance-status-${status === '已通过' ? 'green' : status === '退回' ? 'red' : status === '审核中' ? 'blue' : 'amber'}`}
    >
      {status} · 演示
    </span>
  );
}
function RecordImage({ record, large = false }: { record: GovernanceRecord; large?: boolean }) {
  return record.image ? (
    <img
      className={
        large ? 'governance-record-image governance-record-large' : 'governance-record-image'
      }
      src={record.image}
      alt={`${record.name} 演示图，非实证照片`}
    />
  ) : (
    <div className={`governance-image-placeholder ${large ? 'governance-record-large' : ''}`}>
      <span>◇</span>
      <small>
        {record.category}
        <br />
        暂无对应图片
      </small>
    </div>
  );
}
function Trend() {
  const series = [
    { values: governanceTrend.submitted, color: '#0878ff', label: '提交' },
    { values: governanceTrend.reviewed, color: '#00b786', label: '审核通过（示例）' },
    { values: governanceTrend.returned, color: '#fb474d', label: '退回' },
  ];
  return (
    <section className="portal-card governance-trend-card">
      <div className="portal-card-head">
        <h2>审核工作趋势（近30天）</h2>
        <a href={`${base}/queue?synLive=0`}>查看全部 ›</a>
      </div>
      <div className="governance-chart-legend">
        {series.map((item) => (
          <span key={item.label}>
            <i style={{ background: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
      <svg
        className="governance-chart"
        viewBox="0 0 820 205"
        role="img"
        aria-label="显式演示趋势：提交、审核通过与退回，非实时统计"
      >
        <defs>
          <linearGradient id="governance-chart-fill" x1="0" y1="0" x2="0" y2="1">
            <stop stopColor="#1678ff" stopOpacity=".12" />
            <stop offset="1" stopColor="#1678ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 5, 10, 15, 20].map((v) => (
          <g key={v}>
            <line x1="38" y1={170 - v * 7} x2="795" y2={170 - v * 7} stroke="#e9eff8" />
            <text x="9" y={175 - v * 7}>
              {v}
            </text>
          </g>
        ))}
        {[0, 1, 2, 3, 4, 5, 6].map((v) => (
          <line key={v} x1={38 + v * 126} x2={38 + v * 126} y1="30" y2="170" stroke="#eef3fa" />
        ))}
        <path
          d={`M38 170 ${governanceTrend.submitted.map((v, i) => `L${38 + i * 34.4} ${170 - v * 7}`).join(' ')} L795 170 Z`}
          fill="url(#governance-chart-fill)"
        />
        {series.map((s) => (
          <g key={s.label}>
            <polyline
              points={s.values.map((v, i) => `${38 + i * 34.4},${170 - v * 7}`).join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth="1.7"
            />
            {s.values.map((v, i) => (
              <circle key={i} cx={38 + i * 34.4} cy={170 - v * 7} r="2.5" fill={s.color} />
            ))}
          </g>
        ))}
        {['11-10', '11-15', '11-20', '11-25', '11-30', '12-05', '12-10'].map((label, i) => (
          <text key={label} x={20 + i * 126} y="197">
            {label}
          </text>
        ))}
      </svg>
      <p className="governance-chart-note">显式示例序列 · 本地 DEV fixture · 非全国实时数据</p>
    </section>
  );
}
function RecentTable({ records = governanceRecords }: { records?: readonly GovernanceRecord[] }) {
  return (
    <div className="governance-table-wrap">
      <table className="portal-table governance-table">
        <thead>
          <tr>
            <th>提交时间</th>
            <th>资料名称</th>
            <th>类型</th>
            <th>提交方</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.id}>
              <td>{r.submitted.slice(0, 10)}</td>
              <td>{r.name}</td>
              <td>
                <span>{r.kind === 'PRODUCT_MODEL' ? '产品' : '装备'}</span>
                <small>{r.kind}</small>
              </td>
              <td>{r.submitter}</td>
              <td>
                <Status status={r.status} />
              </td>
              <td>
                <a href={queueUrl(r.id)}>查看</a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function Dashboard() {
  const stats = [
    {
      label: '待审核事项',
      value: governanceDemoTotals.pending,
      detail: '产品 5｜装备 3',
      symbol: '▣',
      tone: 'blue',
    },
    {
      label: '退回待处理',
      value: governanceDemoTotals.returned,
      detail: '等待重新提交',
      symbol: '↩',
      tone: 'red',
    },
    {
      label: '今日处理',
      value: governanceDemoTotals.today,
      detail: '已完成审核（示例）',
      symbol: '✓',
      tone: 'green',
    },
    {
      label: '已发布（本月）',
      value: governanceDemoTotals.published,
      detail: '产品 8｜装备 4（示例）',
      symbol: '▥',
      tone: 'purple',
    },
  ];
  return (
    <div className="governance-home">
      <section className="governance-welcome">
        <div>
          <h1>你好，管理员</h1>
          <p>欢迎使用 AI消防+救援 治理与审核后台</p>
        </div>
        <div className="governance-welcome-right">
          <strong>更智能的协同，更安全的救援</strong>
          <small>Smarter Coordination, Safer Rescue With AI</small>
        </div>
      </section>
      <div className="governance-stats">
        {stats.map((s) => (
          <a
            href={`${base}/queue?synLive=0`}
            className={`governance-stat governance-stat-${s.tone}`}
            key={s.label}
          >
            <span className="governance-stat-icon">{s.symbol}</span>
            <div>
              <span>
                {s.label} <small>演示</small>
              </span>
              <strong>
                {s.value}
                <small>个</small>
              </strong>
              <p>{s.detail}</p>
            </div>
          </a>
        ))}
      </div>
      <div className="governance-charts">
        <Trend />
        <section className="portal-card governance-distribution">
          <div className="portal-card-head">
            <h2>资料类型分布</h2>
            <a href={`${base}/queue?synLive=0`}>查看全部 ›</a>
          </div>
          <div className="governance-distribution-body">
            <div
              className="governance-donut"
              role="img"
              aria-label="演示资料总数56，产品32，装备24"
            >
              <div>
                <strong>56</strong>
                <span>总数 · 演示</span>
              </div>
            </div>
            <div className="governance-distribution-labels">
              <p>
                <i />
                产品资料 32（57%）<small>PRODUCT_MODEL</small>
              </p>
              <p>
                <i />
                装备资料 24（43%）<small>LOCAL_ASSET</small>
              </p>
            </div>
          </div>
          <p className="governance-chart-note">显式示例汇总，独立于下方 6 条演示记录</p>
        </section>
      </div>
      <section className="portal-card governance-recent">
        <div className="portal-card-head">
          <h2>最近处理的事项</h2>
          <a href={`${base}/queue?synLive=0`}>查看全部 ›</a>
        </div>
        <RecentTable />
      </section>
    </div>
  );
}
function Versions({ record }: { record: GovernanceRecord }) {
  return <ReviewVersionComparison record={record} />;
}
function Detail({
  record,
  localHistory,
}: {
  record: GovernanceRecord;
  localHistory: readonly string[];
}) {
  const [tab, setTab] = useState('资料详情');
  const [thumbnail, setThumbnail] = useState(0);
  return (
    <section className="portal-card governance-detail">
      <div className="governance-tabs" role="tablist" aria-label="审核资料页签">
        {['资料详情', '技术资料', '审核记录'].map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            aria-controls="governance-tab-content"
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      <div id="governance-tab-content" role="tabpanel" aria-label={tab}>
        {tab === '资料详情' ? (
          <>
            <div className="governance-detail-top">
              <div>
                <div
                  className="governance-main-image"
                  style={{ background: thumbnail === 1 ? '#f0f5fb' : undefined }}
                >
                  <RecordImage record={record} large />
                </div>
                <div className="governance-thumbnails">
                  {[0, 1].map((i) => (
                    <button
                      key={i}
                      onClick={() => setThumbnail(i)}
                      aria-label={i === 0 ? '查看产品演示图' : '查看资料示意图'}
                      aria-pressed={thumbnail === i}
                    >
                      <RecordImage record={record} />
                    </button>
                  ))}
                  <span>
                    包内演示素材
                    <br />
                    非设备实证
                  </span>
                </div>
              </div>
              <div className="governance-detail-info">
                <h2>{record.name}</h2>
                <Status status={record.status} />
                <p className="review-demo-note">原始提交资料状态 · 只读，不随会话意见变更</p>
                <dl>
                  <dt>资料来源</dt>
                  <dd>{record.kind}</dd>
                  <dt>提交方</dt>
                  <dd>{record.submitter}</dd>
                  <dt>提交时间</dt>
                  <dd>{record.submitted}</dd>
                  <dt>产品类别</dt>
                  <dd>{record.category}</dd>
                  <dt>型号</dt>
                  <dd>{record.model}</dd>
                  <dt>版本</dt>
                  <dd>{record.version}</dd>
                  <dt>主要用途</dt>
                  <dd>{record.purpose}</dd>
                  <dt>核心能力</dt>
                  <dd>{record.capabilities}</dd>
                  <dt>适用场景</dt>
                  <dd>{record.scene}</dd>
                </dl>
              </div>
            </div>
            <section className="governance-submission">
              <h3>提交说明</h3>
              <p>
                本次为 {record.model} 的本地演示资料，包含说明书、技术参数与应用描述的样例目录。
                {record.kind === 'PRODUCT_MODEL'
                  ? '厂家用途与能力均为提交方声明，不代表已安装配置或已验证能力。'
                  : '本地资产描述仅供审核查看，不与同名厂商产品自动关联。'}{' '}
                真实性、机构身份和安全性尚未核验。
              </p>
            </section>
            <Versions record={record} />
          </>
        ) : tab === '技术资料' ? (
          <div className="governance-tab-body">
            <h2>技术资料目录</h2>
            <p>所有参数为资料结构演示，未知值保留未知。</p>
            <dl>
              <dt>说明书</dt>
              <dd>{record.model} 产品说明目录 · 样例</dd>
              <dt>检验报告</dt>
              <dd>尚未提供可核验报告</dd>
              <dt>性能证明</dt>
              <dd>未知，等待责任方验证</dd>
              <dt>媒体来源</dt>
              <dd>用户提供设计包演示图，不构成设备证据</dd>
            </dl>
            <Versions record={record} />
          </div>
        ) : (
          <div className="governance-tab-body">
            <h2>审核记录 · DEV 示例</h2>
            <p className="review-demo-note">以下仅08当前会话回放，无正式审计记录。</p>
            <ul>
              {localHistory.map((value, index) => (
                <li key={index}>{value}</li>
              ))}
            </ul>
            <ol className="governance-timeline">
              <li>
                <strong>{record.submitted} · 示例提交</strong>
                <p>
                  {record.submitter} 提供 {record.kind} 资料 v2
                </p>
              </li>
              <li>
                <strong>资料待核对</strong>
                <p>尚未连接正式审核；无正式审批、Owner 发布或授权记录。</p>
              </li>
            </ol>
            <a href={`${base}/queue?synLive=0`}>返回审核队列</a>
          </div>
        )}
      </div>
    </section>
  );
}
interface QueueMemory {
  openerId: string;
  scrollY: number;
  returnPending: boolean;
}
function Queue({
  path,
  session,
  setSession,
  filters,
  setFilters,
  memory,
}: {
  path: string;
  session: ReviewSession;
  setSession: Dispatch<SetStateAction<ReviewSession>>;
  filters: ReviewQueueFilters;
  setFilters: Dispatch<SetStateAction<ReviewQueueFilters>>;
  memory: RefObject<QueueMemory>;
}) {
  const queueRef = useRef<HTMLElement>(null);
  const selectedId = path.split('/queue/')[1];
  const selected = selectedId
    ? governanceRecords.find((r) => r.id === selectedId)
    : (governanceRecords.find((r) => r.id === memory.current.openerId) ?? governanceRecords[0]);
  const selectedCase = selected ? session[selected.id] : undefined;
  const records = filterReviewQueue(filters, session);
  const changeFilter = (patch: Partial<ReviewQueueFilters>) =>
    setFilters((previous) => ({ ...previous, ...patch }));
  useEffect(() => {
    if (!selectedId && memory.current.returnPending) {
      const saved = memory.current;
      const frame = requestAnimationFrame(() => {
        window.scrollTo(0, saved.scrollY);
        const opener = queueRef.current?.querySelector<HTMLElement>(
          `[data-review-id="${saved.openerId}"]`,
        );
        (opener ?? queueRef.current?.querySelector<HTMLElement>('input'))?.focus({
          preventScroll: true,
        });
        saved.returnPending = false;
      });
      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  }, [selectedId, memory]);
  return (
    <>
      <p className="governance-page-intro">
        统一的审核队列与详细审核页面 · 6 条独立08本地演示资料 · 无管理员身份认证 /
        不保存正式审核决定
      </p>
      <div
        className={`governance-audit-layout ${selectedId ? 'review-demo-show-detail' : 'review-demo-show-list'}`}
      >
        <section ref={queueRef} className="portal-card governance-queue">
          <div className="portal-card-head">
            <h2>待审核列表（{records.length}）</h2>
            <button
              className="governance-reset"
              onClick={() => setFilters({ ...initialReviewFilters })}
            >
              ⟳ 重置筛选
            </button>
          </div>
          <div className="governance-filters">
            <select
              aria-label="资料类型筛选"
              value={filters.kind}
              onChange={(e) => changeFilter({ kind: e.target.value })}
            >
              <option value="ALL">全部类型</option>
              <option value="PRODUCT_MODEL">产品资料</option>
              <option value="LOCAL_ASSET">装备资料</option>
            </select>
            <select
              aria-label="资料状态筛选"
              value={filters.status}
              onChange={(e) => changeFilter({ status: e.target.value })}
            >
              <option value="ALL">全部状态</option>
              {Object.entries(reviewStatusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <button onClick={() => changeFilter({ oldest: !filters.oldest })}>
              提交时间 {filters.oldest ? '↑' : '↓'}
            </button>
          </div>
          <div className="review-demo-secondary-filters">
            <label>
              提交版本
              <select
                aria-label="提交版本筛选"
                value={filters.version}
                onChange={(e) => changeFilter({ version: e.target.value })}
              >
                <option value="ALL">全部版本</option>
                <option value="v1">v1</option>
                <option value="v2">v2</option>
                <option value="v3">v3</option>
              </select>
            </label>
            <label>
              来源范围
              <select
                aria-label="来源范围筛选"
                value={filters.scope}
                onChange={(e) => changeFilter({ scope: e.target.value })}
              >
                <option value="ALL">全部演示范围</option>
                {governanceRecords.map((r) => (
                  <option key={r.id} value={reviewScope(r)}>
                    {r.submitter} · {r.model}
                  </option>
                ))}
              </select>
            </label>
            <label>
              提交起始日期
              <input
                type="date"
                aria-label="提交起始日期"
                value={filters.since}
                onChange={(e) => changeFilter({ since: e.target.value })}
              />
            </label>
          </div>
          <label className="governance-search">
            <span>⌕</span>
            <input
              aria-label="搜索审核资料"
              placeholder="搜索产品名称、型号或提交方…"
              value={filters.query}
              onChange={(e) => changeFilter({ query: e.target.value })}
            />
          </label>
          <div className="governance-queue-list">
            {records.map((r) => (
              <a
                href={queueUrl(r.id)}
                key={r.id}
                data-review-id={r.id}
                aria-current={selected?.id === r.id ? 'page' : undefined}
                className={`governance-queue-item ${selected?.id === r.id ? 'is-selected' : ''}`}
                onClick={() => {
                  memory.current = { openerId: r.id, scrollY: window.scrollY, returnPending: true };
                }}
              >
                <RecordImage record={r} />
                <div>
                  <strong>{r.name}</strong>
                  <small>{r.submitter}</small>
                  <small>
                    {r.kind}｜{r.version}
                  </small>
                  <small>{r.submitted}</small>
                  <span className="governance-status governance-status-blue">
                    {reviewStatusLabels[session[r.id]?.status ?? 'PENDING_REVIEW']} · 演示
                  </span>
                  <small>{session[r.id]?.status}</small>
                </div>
              </a>
            ))}
            {records.length === 0 && (
              <div className="governance-empty">没有匹配的演示资料。请调整筛选条件。</div>
            )}
          </div>
          <div className="governance-queue-footer">
            已展示全部 {records.length} 条本地样例 · 未接收05/06会话提交
          </div>
          <a href={`${base}?synLive=0`} className="governance-back">
            ‹ 返回治理工作台
          </a>
        </section>
        {selected && selectedCase ? (
          <div className="review-demo-detail-wrap">
            <a className="review-demo-back-to-list" href={`${base}/queue?synLive=0`}>
              ‹ 返回审核队列（保留筛选）
            </a>
            <Detail key={selected.id} record={selected} localHistory={selectedCase.history} />
            <ReviewQueuePrototype
              record={selected}
              item={selectedCase}
              onOpinion={(opinion) =>
                setSession((previous) => updateReviewCase(previous, selected.id, { opinion }))
              }
              onScenario={(scenario) =>
                setSession((previous) => updateReviewCase(previous, selected.id, { scenario }))
              }
              onAction={(action) =>
                setSession((previous) => applyReviewDemoAction(previous, selected.id, action))
              }
              onReset={() => setSession((previous) => resetReviewDemoCase(previous, selected.id))}
              onReadback={() => setSession((previous) => noteReviewReadback(previous, selected.id))}
            />
          </div>
        ) : (
          <section className="portal-card governance-empty review-demo-detail-wrap">
            <h2>资料未知或不存在</h2>
            <p>该标识不在本地演示集合中。</p>
            <a href={`${base}/queue?synLive=0`}>返回审核队列</a>
          </section>
        )}
      </div>
    </>
  );
}
function Skeleton({ section }: { section: string }) {
  const information: Record<
    string,
    { title: string; description: string; items: readonly string[] }
  > = {
    products: {
      title: '产品管理',
      description: '06 产品资料的治理查看与审核视图',
      items: [
        '来源：PRODUCT_MODEL · 厂商产品声明',
        '治理内容：资料质量、版本与发布协调',
        '正式 Owner 发布等待 BIZ-04 授权',
      ],
    },
    assets: {
      title: '装备管理',
      description: '05 本地资产资料的治理查看与审核视图',
      items: [
        '来源：LOCAL_ASSET · 机构本地资产描述',
        '本地配置、可用性与厂商声明分别核对',
        '当前不连接或修改 05 权威装备链',
      ],
    },
    organizations: {
      title: '用户与机构',
      description: '机构身份与治理责任结构预览',
      items: [
        '厂商：提供产品资料与用途声明',
        '装备机构：提供本地资产与配置描述',
        '治理人员：审阅资料，正式权限尚未接入',
      ],
    },
    settings: {
      title: '系统设置',
      description: '治理规范、资料来源与审计需求预览',
      items: [
        '审核规则：资料完整性、来源、风险提示',
        '发布协调：责任 Owner 与版本审计待接入',
        '身份与权限：只读占位，不修改真实权限',
      ],
    },
  };
  const item = information[section];
  if (!item)
    return (
      <section className="portal-card governance-empty">
        <h1>页面未知</h1>
        <a href={`${base}?synLive=0`}>返回工作台</a>
      </section>
    );
  const records =
    section === 'products'
      ? governanceRecords.filter((r) => r.kind === 'PRODUCT_MODEL')
      : section === 'assets'
        ? governanceRecords.filter((r) => r.kind === 'LOCAL_ASSET')
        : [];
  return (
    <div className="governance-skeleton">
      <section className="governance-welcome">
        <div>
          <h1>{item.title}</h1>
          <p>{item.description}</p>
        </div>
      </section>
      <section className="portal-card governance-skeleton-card">
        <h2>治理资料结构</h2>
        <ul>
          {item.items.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
        <p>本地交互原型 · DEMO_ONLY · 非正式审核</p>
        <button className="portal-button" disabled>
          正式业务后续接入
        </button>{' '}
        <a href={`${base}/queue?synLive=0`}>查看演示审核资料 ›</a>
      </section>
      {records.length > 0 && (
        <section className="portal-card governance-recent">
          <h2>演示资料 · 只读查看</h2>
          <RecentTable records={records} />
        </section>
      )}
      <a href={`${base}?synLive=0`}>‹ 返回治理工作台</a>
    </div>
  );
}
export function GovernancePortal({
  path = typeof window === 'undefined' ? base : window.location.pathname,
}: {
  path?: string;
}) {
  const [reviewSession, setReviewSession] = useState<ReviewSession>(createReviewSession);
  const [reviewFilters, setReviewFilters] = useState<ReviewQueueFilters>(() => ({
    ...initialReviewFilters,
  }));
  const queueMemory = useRef<QueueMemory>({ openerId: '', scrollY: 0, returnPending: false });
  const route = path.replace(/\/$/, '');
  const section = route.slice(base.length).split('/')[1] ?? '';
  const sectionTitles: Record<string, string> = {
    products: '产品管理',
    assets: '装备管理',
    organizations: '用户与机构',
    settings: '系统设置',
  };
  const title =
    section === 'queue'
      ? '08 管理端 — 审核中心'
      : section === ''
        ? '08 管理端 — 工作台（首页）'
        : `08 管理端 — ${sectionTitles[section] ?? '页面未知'}`;
  if (synLiveEnabled()) {
    // Same rule as the 06 portal: the confirmed shell, navigation and queue layout stay, and only
    // the 08 facts come from the durable reads.
    return (
      <PortalShell
        portal="governance"
        title={section === 'queue' ? '08 管理端 — 审核中心 LIVE' : title}
        mode="SYN_LIVE"
      >
        <div className="governance-portal">
          <SynLiveProductReviewProvider role="reviewer">
            <SynLiveSessionPanel />
            {section === '' ? (
              <AdminWorkbench />
            ) : section === 'queue' || section === 'products' ? (
              <SynLiveReviewQueue />
            ) : null}
            {/*
                  The SAME 审核中心 page, the LOCAL_ASSET half of the queue. The two panels are
                  separate because they are reviewed through different Owners and published through
                  different surfaces, so each keeps a single wire; the page, its navigation and its
                  route stay one.
                */}
            {(section === 'queue' || section === 'assets') && <SynLiveAssetReviewQueue />}
            {!['', 'queue', 'products', 'assets'].includes(section) && (
              <section className="portal-card governance-skeleton-card">
                <h1>{sectionTitles[section] ?? '页面未接入'}</h1>
                <p>当前后端尚未实现此管理接口，暂不可操作；不会使用演示记录替代。</p>
              </section>
            )}
          </SynLiveProductReviewProvider>
        </div>
      </PortalShell>
    );
  }
  return (
    <PortalShell portal="governance" title={title}>
      <div className="governance-portal">
        {section === '' ? (
          <Dashboard />
        ) : section === 'queue' ? (
          <Queue
            path={route}
            session={reviewSession}
            setSession={setReviewSession}
            filters={reviewFilters}
            setFilters={setReviewFilters}
            memory={queueMemory}
          />
        ) : (
          <Skeleton section={section} />
        )}
      </div>
    </PortalShell>
  );
}
